import {
    getApiErrorMessage,
    getFormMode,
    getRecordFromResponse,
    getResponseData,
    validateSchemaFields
} from 'lib/formUtils';
import {
    getCodUsuarioRegistrou,
    getCurrencyInputValue,
    isMissingValue,
    parseCurrencyInputValue
} from 'lib/functions';
import {
    ComboPadrao,
    FormActions,
    FormStatusSwitch,
    FormTextArea
} from 'views/Components';
import {
    Box,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    TextField,
    Typography
} from '@material-ui/core';
import api from 'services/api';
import PropTypes from 'prop-types';
import { ANAO, ASIM } from 'consts';
import React, { Component } from 'react';
import { safeSetState } from 'lib/reactUtils';
import { validateComboFields } from 'lib/configUtils';
import { AlertInformation } from 'components';

/* Campos que seguem uppercase */
const UPPERCASE_FIELDS = [
    'descmercadoria',
    'codigodebarras',
    'ncm'
];

/* Filtro do combo de centro de despesa (débito e analítico) */
const CENTRO_DESPESA_QUERY = '?tipolancamento=D&tipoconta=A&limit=10000';

/* Retorna a mercadoria padrão */
const getMercadoriaDefault = () => ({
    codmercadoria: '',
    descmercadoria: '',
    inativo: ANAO,
    altogiro: ANAO,
    codgrupomercadoria: '',
    codunidade: '',
    codigodebarras: '',
    ncm: '',
    estoqueminimo: '',
    estoquemaximo: '',
    codcentrocusto: '',
    especificacao: ''
});

class MercadoriaForm extends Component {
    state = {
        isLoading: true,
        isNewRecord: false,
        isView: false,
        openAlert: false,
        messageAlert: '',
        grupos: [],
        unidades: [],
        centros: [],
        errors: {},
        mercadoria: getMercadoriaDefault(),
        schema: {
            descmercadoria: {
                presence: {
                    allowEmpty: false,
                    message: 'Descrição da mercadoria obrigatória'
                },
                length: { maximum: 50 },
                type: 'string'
            },
            codgrupomercadoria: {
                presence: {
                    allowEmpty: false,
                    message: 'Grupo de mercadoria obrigatório'
                },
                type: 'integer'
            },
            codunidade: {
                presence: {
                    allowEmpty: false,
                    message: 'Unidade obrigatória'
                },
                type: 'integer'
            }
        }
    };

    _isMounted = false;
    safeSetState = safeSetState;

    /* Carrega os dados iniciais da tela */
    async componentDidMount() {
        this._isMounted = true;

        const {
            id,
            isNewRecord,
            isView
        } = getFormMode(this.props.match, this.props.location);

        try {
            if (isNewRecord) {
                const [grupos, unidades, centros] = await Promise.all([
                    this.loadGrupos(),
                    this.loadUnidades(),
                    this.loadCentros()
                ]);

                this.safeSetState({
                    isNewRecord: true,
                    isView: false,
                    isLoading: false,
                    mercadoria: getMercadoriaDefault(),
                    grupos,
                    unidades,
                    centros
                });

                return;
            }

            // o código é VARCHAR (pode ser alfanumérico), por isso não valida se é número
            if (id) {
                await this.loadMercadoria(id, isView);

                return;
            }

            this.safeSetState({ isLoading: false });
        } catch (error) {
            console.error(
                'Erro ao carregar a mercadoria:',
                error.response?.data || error.message
            );

            this.handleOpenAlert(
                getApiErrorMessage(error, 'Erro ao carregar a mercadoria!')
            );

            this.safeSetState({ isLoading: false });
        }
    };

    /* Limpa a flag de montagem ao sair da tela */
    componentWillUnmount() {
        this._isMounted = false;
    };

    /* Carrega os grupos de mercadoria */
    loadGrupos = async (codgrupomercadoria) => {
        const url = codgrupomercadoria
            ? `/grupomercadoria/listforchange/${codgrupomercadoria}`
            : '/grupomercadoria/active/true?limit=10000';

        const response = await api.get(url);

        return getResponseData(response);
    };

    /* Carrega as unidades */
    loadUnidades = async (codunidade) => {
        const url = codunidade
            ? `/unidade/listforchange/${codunidade}`
            : '/unidade/active/true?limit=10000';

        const response = await api.get(url);

        return getResponseData(response);
    };

    /* Carrega os centros de despesa */
    loadCentros = async (codcentrocusto) => {
        const url = codcentrocusto
            ? `/financas/centroreceitadespesa/listforchange/${codcentrocusto}${CENTRO_DESPESA_QUERY}`
            : `/financas/centroreceitadespesa/active/true${CENTRO_DESPESA_QUERY}`;

        const response = await api.get(url);
        const centros = getResponseData(response);

        // monta a descrição já com a classificação pro combo
        return centros.map(centro => ({
            ...centro,
            desccentro: centro.classificacao
                ? `${centro.classificacao} - ${centro.desccreditodebito}`
                : centro.desccreditodebito
        }));
    };

    /* Carrega a mercadoria */
    loadMercadoria = async (id, isView) => {
        const { history } = this.props;

        const response = await api.get(`/mercadoria/${id}`);
        const registro = getRecordFromResponse(response);

        if (!registro) {
            this.handleOpenAlert('Mercadoria não encontrada!');

            history.push('/estoque/mercadoria');

            return;
        }

        const [grupos, unidades, centros] = await Promise.all([
            this.loadGrupos(registro.codgrupomercadoria),
            this.loadUnidades(registro.codunidade),
            this.loadCentros(registro.codcentrocusto)
        ]);

        this.safeSetState({
            mercadoria: this.normalizaMercadoria(registro),
            grupos,
            unidades,
            centros,
            isNewRecord: false,
            isView,
            isLoading: false
        });
    };

    /* Normaliza o registro vindo do backend (null vira vazio, ids viram string) */
    normalizaMercadoria = (registro) => {
        const padrao = getMercadoriaDefault();

        const camposId = [
            'codgrupomercadoria',
            'codunidade',
            'codcentrocusto'
        ];

        const mercadoria = { ...padrao };

        Object.keys(padrao).forEach(campo => {
            const valor = registro[campo];

            if (valor === null || valor === undefined) {
                return;
            }

            mercadoria[campo] = camposId.includes(campo) ? String(valor) : valor;
        });

        return {
            ...mercadoria,
            inativo: registro.inativo || ANAO,
            altogiro: registro.altogiro || ANAO
        };
    };

    /* Evento onChange componentes */
    onChange = (event) => {
        const { errors } = this.state;
        const { name, type, value, checked } = event.target;

        if (errors[name]) {
            this.setState(prev => ({
                errors: {
                    ...prev.errors,
                    [name]: undefined
                }
            }));
        }

        let adjustedValue;

        if (type === 'checkbox' && name === 'inativo') {
            adjustedValue = checked ? ANAO : ASIM;
        } else if (type === 'checkbox') {
            adjustedValue = checked ? ASIM : ANAO;
        } else if (UPPERCASE_FIELDS.includes(name)) {
            adjustedValue = String(value).toUpperCase();
        } else {
            adjustedValue = value;
        }

        this.setState(prevState => ({
            mercadoria: {
                ...prevState.mercadoria,
                [name]: adjustedValue
            }
        }));
    };

    /* Trata os campos de estoque (valores decimais) */
    handleChangeValorMonetario = (event) => {
        const { name, value } = event.target;
        const numero = parseCurrencyInputValue(value);

        this.setState(prevState => ({
            mercadoria: {
                ...prevState.mercadoria,
                [name]: numero
            }
        }));
    };

    /* Abre a janela de alerta com mensagem */
    handleOpenAlert = (message) => {
        this.safeSetState({
            openAlert: true,
            messageAlert: message
        });
    };

    /* Fecha a janela de mensagem alerta */
    handleCloseAlert = () => {
        this.setState({ openAlert: false });
    };

    /* Retorna o payload da mercadoria */
    getPayloadMercadoria = () => {
        const { mercadoria } = this.state;
        const codusuarioregistrou = getCodUsuarioRegistrou();

        if (isMissingValue(codusuarioregistrou)) {
            throw new Error('Código do usuário não encontrado!');
        }

        return {
            descmercadoria: mercadoria.descmercadoria || '',
            inativo: mercadoria.inativo || ANAO,
            altogiro: mercadoria.altogiro || ANAO,
            especificacao: mercadoria.especificacao || '',
            codunidade: mercadoria.codunidade || '',
            codgrupomercadoria: mercadoria.codgrupomercadoria || '',
            codigodebarras: mercadoria.codigodebarras || '',
            ncm: mercadoria.ncm || '',
            estoqueminimo: mercadoria.estoqueminimo ?? '',
            estoquemaximo: mercadoria.estoquemaximo ?? '',
            codcentrocusto: mercadoria.codcentrocusto || '',
            codusuarioregistrou
        };
    };

    /* Valida os campos obrigatórios ao salvar */
    validationRequiredFields() {
        const { mercadoria, schema } = this.state;

        return validateSchemaFields(
            mercadoria,
            schema,
            [
                'descmercadoria',
                'codgrupomercadoria',
                'codunidade'
            ]
        );
    };

    /* Clicar para salvar */
    onClickSave = async () => {
        const { isNewRecord, mercadoria } = this.state;
        const { history, loadMercadorias } = this.props;

        const errors = this.validationRequiredFields();
        this.setState({ errors });

        if (Object.keys(errors).length > 0) {
            this.handleOpenAlert('Verifique os campos obrigatórios!');

            return;
        }

        const comboErrors = validateComboFields(mercadoria, [
            {
                field: 'codgrupomercadoria',
                message: 'Selecione um grupo de mercadoria válido'
            },
            {
                field: 'codunidade',
                message: 'Selecione uma unidade válida'
            }
        ]);

        if (Object.keys(comboErrors).length > 0) {
            this.setState(prev => ({
                errors: {
                    ...prev.errors,
                    ...comboErrors
                }
            }));

            this.handleOpenAlert(
                'Selecione registros válidos nos campos indicados em vermelho!'
            );

            return;
        }

        try {
            const payload = this.getPayloadMercadoria();

            if (isNewRecord) {
                await api.post('/mercadoria', payload);
            } else {
                await api.put(`/mercadoria/${mercadoria.codmercadoria}`, payload);
            }

            if (loadMercadorias) loadMercadorias();

            history.push('/estoque/mercadoria');
        } catch (error) {
            console.error('Erro ao salvar:', error.response?.data || error.message);

            this.handleOpenAlert(
                getApiErrorMessage(error, 'Erro ao salvar!')
            );
        }
    };

    /* Clicar para cancelar */
    onClickCancel = () => {
        this.props.history.push('/estoque/mercadoria');
    };

    render() {
        const {
            centros,
            errors,
            grupos,
            isLoading,
            isView,
            mercadoria,
            messageAlert,
            openAlert,
            unidades
        } = this.state;

        if (isLoading) {
            return (
                <CardContent>
                    <Typography variant="body1">
                        Carregando mercadoria...
                    </Typography>
                </CardContent>
            );
        }

        return (
            <div>
                <CardContent>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{ flexWrap: 'wrap' }}
                    >
                        <Typography
                            component="h1"
                            variant="h3"
                        >
                            Mercadoria {mercadoria.codmercadoria}
                        </Typography>

                        <FormStatusSwitch
                            checked={mercadoria.inativo === ANAO}
                            name="inativo"
                            onChange={this.onChange}
                            disabled={isView}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        alignItems="center"
                        style={{
                            flexWrap: 'wrap',
                            gap: 16
                        }}
                    >
                        <TextField
                            label="Descrição"
                            name="descmercadoria"
                            value={mercadoria.descmercadoria || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            helperText={errors.descmercadoria || ''}
                            error={!!errors.descmercadoria}
                            disabled={isView}
                            inputProps={{ maxLength: 50 }}
                            style={{
                                flex: 1,
                                minWidth: 260
                            }}
                        />

                        <FormControlLabel
                            label={'Alto giro'}
                            control={
                                <Checkbox
                                    checked={mercadoria.altogiro === ASIM}
                                    name="altogiro"
                                    onChange={this.onChange}
                                    color="primary"
                                    disabled={isView}
                                />
                            }
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <FormControl
                            variant="outlined"
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 240
                            }}
                        >
                            <ComboPadrao
                                name={'codgrupomercadoria'}
                                label={'Grupo'}
                                value={mercadoria.codgrupomercadoria}
                                onChange={this.onChange}
                                fieldId={'codgrupomercadoria'}
                                listItems={grupos}
                                fieldDescription={'descgrupomercadoria'}
                                error={!!errors.codgrupomercadoria}
                                helperText={errors.codgrupomercadoria || ''}
                            />
                        </FormControl>

                        <FormControl
                            variant="outlined"
                            disabled={isView}
                            style={{
                                flex: 1,
                                minWidth: 200
                            }}
                        >
                            <ComboPadrao
                                name={'codunidade'}
                                label={'Unidade'}
                                value={mercadoria.codunidade}
                                onChange={this.onChange}
                                fieldId={'codunidade'}
                                listItems={unidades}
                                fieldDescription={'descunidade'}
                                error={!!errors.codunidade}
                                helperText={errors.codunidade || ''}
                            />
                        </FormControl>
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Código de barras"
                            name="codigodebarras"
                            value={mercadoria.codigodebarras || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            inputProps={{ maxLength: 20 }}
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        />

                        <TextField
                            label="NCM"
                            name="ncm"
                            value={mercadoria.ncm || ''}
                            variant="outlined"
                            onChange={this.onChange}
                            disabled={isView}
                            inputProps={{ maxLength: 20 }}
                            style={{
                                flex: 1,
                                minWidth: 140
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 12 }}
                    >
                        <TextField
                            label="Estoque mínimo"
                            name="estoqueminimo"
                            value={getCurrencyInputValue(mercadoria.estoqueminimo)}
                            variant="outlined"
                            onChange={this.handleChangeValorMonetario}
                            disabled={isView}
                            type="text"
                            style={{
                                flex: 1,
                                minWidth: 140
                            }}
                        />

                        <TextField
                            label="Estoque máximo"
                            name="estoquemaximo"
                            value={getCurrencyInputValue(mercadoria.estoquemaximo)}
                            variant="outlined"
                            onChange={this.handleChangeValorMonetario}
                            disabled={isView}
                            type="text"
                            style={{
                                flex: 1,
                                minWidth: 140
                            }}
                        />
                    </Box>

                    <br />

                    <Box
                        display="flex"
                        flexWrap="wrap"
                    >
                        <FormControl
                            variant="outlined"
                            disabled={isView}
                            fullWidth
                        >
                            <ComboPadrao
                                name={'codcentrocusto'}
                                label={'Centro de despesa'}
                                value={mercadoria.codcentrocusto}
                                onChange={this.onChange}
                                fieldId={'codcreditodebito'}
                                listItems={centros}
                                fieldDescription={'desccentro'}
                            />
                        </FormControl>
                    </Box>

                    <br />

                    <Box>
                        <Typography
                            color="textSecondary"
                            component="p"
                            variant="body2"
                        >
                            Especificação
                        </Typography>

                        <FormTextArea
                            name="especificacao"
                            value={mercadoria.especificacao}
                            onChange={this.onChange}
                            disabled={isView}
                            minHeight={80}
                        />
                    </Box>

                    <br />
                    <br />

                    <FormActions
                        onSave={this.onClickSave}
                        onCancel={this.onClickCancel}
                        disableSave={isView}
                    />
                </CardContent>

                <AlertInformation
                    open={openAlert}
                    message={messageAlert}
                    handleClose={this.handleCloseAlert}
                />
            </div>
        );
    }
};

MercadoriaForm.propTypes = {
    history: PropTypes.object,
    match: PropTypes.object,
    location: PropTypes.object,
    loadMercadorias: PropTypes.func
};

export default MercadoriaForm;